// src/components/legal/AppTerms.jsx
//
// /conditions-utilisation - the terms of use of the YummiTracker app (the
// public side; the professionals' terms are /conditions-generales). Its content
// follows what the code actually does: no account, the device id; a rating
// within 20 m, once a day (routes/validate.ts); the lists, photos and comments
// (utils/sharedLists.ts, routes/listSocial.ts): a pseudo approved by an admin,
// lists and photos reviewed before they are public, comments public at once and
// hidden after REPORT_THRESHOLD reports, photos and comments kept for those who
// rated the place; « Effacer mes données » (routes/myData.ts).
// [À COMPLÉTER] marks what only the company can fill in. A draft: to be
// reviewed by a lawyer before use.
import React from 'react';
import { Box, Container, Typography, Link } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';

const TODO = (label) => <Box component="span" sx={{ bgcolor: '#FFF3CD', px: 0.5 }}>[À COMPLÉTER : {label}]</Box>;

export default function AppTerms() {
  const h2 = { mt: 4, mb: 1, fontWeight: 800 };
  const p = { mb: 1.5 };
  return (
    <Container maxWidth="md" sx={{ py: 5 }}>
      <Typography variant="h3" component="h1" sx={{ fontWeight: 900, mb: 1 }}>Conditions d'utilisation de YummiTracker</Typography>
      <Typography color="text.secondary">Application YummiTracker · mise à jour le 2 novembre 2026</Typography>

      <Typography variant="h5" component="h2" sx={h2}>1. Objet</Typography>
      <Typography sx={p}>
        Les présentes conditions régissent l'utilisation de l'application mobile YummiTracker et des pages publiques de
        qrproofs.com qui en affichent le contenu (listes partagées, profils), éditées par {TODO('raison sociale')} (« nous »,
        voir les <Link component={RouterLink} to="/mentions-legales">mentions légales</Link>). En utilisant l'application,
        vous acceptez ces conditions. L'application est gratuite.
      </Typography>
      <Typography sx={p}>
        Les établissements qui s'abonnent à QRProof relèvent de conditions distinctes : les{' '}
        <Link component={RouterLink} to="/conditions-generales">conditions générales de vente et d'utilisation</Link>.
      </Typography>

      <Typography variant="h5" component="h2" sx={h2}>2. Sans compte</Typography>
      <Typography sx={p}>
        YummiTracker ne demande ni compte, ni e-mail, ni mot de passe. Ce que vous faites dans l'application est rattaché à
        un identifiant aléatoire créé sur votre téléphone. Si vous désinstallez l'application ou changez de téléphone, nous
        ne pouvons pas vous rendre vos listes ni votre pseudo : ils ne sont liés à aucun compte.
      </Typography>
      <Typography sx={p}>
        Vous devez avoir au moins 15 ans pour publier du contenu (pseudo, listes, photos, commentaires), ou l'accord d'un
        parent.
      </Typography>

      <Typography variant="h5" component="h2" sx={h2}>3. Les notes</Typography>
      <Typography sx={p}>
        Vous notez un établissement en scannant le QR code affiché sur place. Une note n'est acceptée que si votre téléphone
        se trouve à moins de 20 mètres de l'établissement, et une seule fois par jour pour un même établissement (une fois
        par séjour pour un hébergement). La position est demandée uniquement pour cette vérification. Ne notez que les
        établissements où vous êtes réellement allé : toute tentative de contourner ces vérifications (fausse position,
        notes en série, notes achetées ou demandées en échange d'un avantage) est interdite, et les notes concernées peuvent
        être supprimées.
      </Typography>

      <Typography variant="h5" component="h2" sx={h2}>4. Pseudo, listes, photos et commentaires</Typography>
      <Typography component="div" sx={p}>
        Pour publier, vous choisissez un pseudo. Les règles :
        <ul>
          <li>
            <b>Pseudo :</b> 3 à 24 caractères. Il n'est affiché qu'une fois validé par notre équipe ; d'ici là, vos
            publications apparaissent sous « Membre YummiTracker ». Un pseudo trompeur, injurieux ou qui imite une autre
            personne, une marque ou notre équipe est refusé.
          </li>
          <li>
            <b>Listes :</b> 30 au plus par téléphone. Une liste n'est publique qu'après validation par notre équipe, et
            chaque modification d'une liste publique est validée à son tour.
          </li>
          <li>
            <b>Photos :</b> vous ne pouvez publier une photo d'un établissement QRProof que si vous l'avez noté sur place.
            Chaque photo est vérifiée par notre équipe avant d'être visible. L'établissement peut y répondre.
          </li>
          <li>
            <b>Profil :</b> une fois votre première liste publiée, vous pouvez ajouter une photo, une courte bio et des
            liens vers vos réseaux. Ils ne sont visibles qu'après validation par notre équipe, et tant que vous avez une
            liste publique. Les liens doivent mener à vos propres comptes.
          </li>
          <li>
            <b>Commentaires :</b> réservés à ceux qui ont noté sur place au moins un lieu de la liste. Ils sont publiés tout
            de suite.
          </li>
        </ul>
        Ce que vous publiez est visible de tous, dans l'application et sur qrproofs.com, et peut être partagé par lien.
      </Typography>

      <Typography variant="h5" component="h2" sx={h2}>5. Ce qui est interdit</Typography>
      <Typography component="div" sx={p}>
        Il est interdit de publier :
        <ul>
          <li>des propos injurieux, haineux, discriminatoires, violents, sexuels ou du harcèlement ;</li>
          <li>des informations fausses ou diffamatoires sur un établissement ou une personne ;</li>
          <li>des photos de personnes reconnaissables sans leur accord, ou des photos sans rapport avec le lieu ;</li>
          <li>des contenus dont vous n'avez pas les droits (photos d'autrui, textes copiés) ;</li>
          <li>de la publicité, des liens commerciaux ou des avis rédigés pour le compte d'un établissement ;</li>
          <li>des données personnelles d'autrui (numéro, adresse, etc.).</li>
        </ul>
        Il est aussi interdit d'utiliser des robots ou des scripts, ou de perturber le fonctionnement du service.
      </Typography>

      <Typography variant="h5" component="h2" sx={h2}>6. Signalements et modération</Typography>
      <Typography sx={p}>
        Chacun peut signaler une liste, une photo ou un commentaire depuis l'application. Un contenu signalé par plusieurs
        personnes (3 signalements) est masqué en attendant que notre équipe l'examine. Nous pouvons refuser, masquer ou
        supprimer tout contenu contraire à ces conditions ou à la loi, et bloquer la publication depuis un téléphone qui
        enfreint ces règles de façon répétée. Quand une photo ou une liste est refusée, le motif vous est indiqué dans
        l'application. Pour contester une décision ou nous signaler un contenu illicite, écrivez à {TODO('e-mail de contact')}.
      </Typography>

      <Typography variant="h5" component="h2" sx={h2}>7. Vos contenus</Typography>
      <Typography sx={p}>
        Vous restez propriétaire de ce que vous publiez. Vous nous autorisez, gratuitement et pour le monde entier, à
        l'héberger, l'afficher, le redimensionner et le partager dans l'application, sur qrproofs.com et dans les aperçus
        des liens de partage, tant qu'il est en ligne. Vous garantissez avoir les droits sur vos photos et textes. Cette
        autorisation prend fin quand vous supprimez le contenu ou effacez vos données.
      </Typography>

      <Typography variant="h5" component="h2" sx={h2}>8. Vos données</Typography>
      <Typography sx={p}>
        Le traitement de vos données est décrit dans la{' '}
        <Link component={RouterLink} to="/confidentialite">politique de confidentialité</Link>. Vous pouvez tout effacer à
        tout moment depuis l'application (Paramètres › Effacer mes données) : voir{' '}
        <Link component={RouterLink} to="/suppression-donnees">supprimer vos données</Link>.
      </Typography>

      <Typography variant="h5" component="h2" sx={h2}>9. Disponibilité et responsabilité</Typography>
      <Typography sx={p}>
        Nous faisons de notre mieux pour que le service fonctionne, sans garantir qu'il soit toujours disponible ni sans
        erreur. Les informations sur les établissements (horaires, événements, notes) sont données à titre indicatif ;
        vérifiez-les auprès de l'établissement. Les notes, listes, photos et commentaires expriment l'avis de leurs auteurs,
        pas le nôtre. Nous ne sommes responsables d'un contenu publié par un utilisateur qu'à partir du moment où il nous a
        été signalé comme manifestement illicite et que nous ne l'avons pas retiré promptement.
      </Typography>

      <Typography variant="h5" component="h2" sx={h2}>10. Modifications</Typography>
      <Typography sx={p}>
        Nous pouvons faire évoluer l'application et ces conditions. La version applicable est celle publiée sur cette page ;
        en cas de changement important, nous l'indiquerons dans l'application.
      </Typography>

      <Typography variant="h5" component="h2" sx={h2}>11. Droit applicable</Typography>
      <Typography sx={p}>
        Ces conditions sont soumises au droit français. En cas de litige, contactez-nous d'abord à {TODO('e-mail de contact')} ;
        à défaut d'accord, les tribunaux français sont compétents, sous réserve des règles protégeant le consommateur.
      </Typography>
    </Container>
  );
}
